import {
    ChevronLeft,
    ChevronRight,
    HelpOutline,
    LogoutRounded,
    NotificationsNoneOutlined,
} from "@mui/icons-material";
import {
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    Popper,
    Slide,
    styled,
} from "@mui/material";
import React, { Fragment } from "react";
import { Link, LinkProps, useHistory, useLocation } from "react-router-dom";
import {
    closeAlerts,
    closeSidebar,
    openAlerts,
    openSidebar,
} from "../../reducers/common";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getColorFromName, hexToRgb } from "./utils/utils";
import LinkChain from "./customIcons/LinkChain";
import CopyCircle from "./customIcons/CopyCircle";
import { setSuccessMessage } from "../../actions/common";
import { getServerName } from "../../actions/yardidata";

interface StyledListItemProps {
    selected: boolean;
}

const StyledBadge = styled(Badge)(({ theme }) => ({
    width: "100%",
    ".MuiBadge-badge": {
        right: "unset",
        left: "32px",
        top: "10px",
    },
}));

const StyledFooter = styled("div")(({ theme }) => ({
    marginTop: "auto",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    flexBasis: "260px",
    ".MuiDivider-root": {
        width: "100%",
        background: "rgba(255, 255, 255, 0.15)",
    },
}));

const StyledListItem = styled(ListItemButton, {
    shouldForwardProp: (p) => !["selected"].includes(p.toString()),
})<StyledListItemProps>(({ theme, selected }) => ({
    marginBottom: 8,
    paddingTop: "10px",
    paddingLeft: "22px",
    marginLeft: "-8px",
    paddingBottom: "12px",
    height: "48px",
    borderRadius: "0px 30px 30px 0px",
    color: theme.palette.primary.contrastText,
    overflow: "hidden",
    ".MuiSvgIcon-root": {
        color: theme.palette.primary.contrastText,
    },
    ".MuiListItemText-primary": {
        fontWeight: "bold",
        fontSize: "0.825rem",
    },
    ".MuiListItemAvatar-root": {
        marginLeft: -4,
        marginRight: 4,
        ".MuiAvatar-root": {
            height: "32px",
            width: "32px",
            fontSize: "11pt",
        },
    },
    "&:hover": {
        background: `rgba(${hexToRgb("#6F4EE8", "string")}, 0.2)`,
    },
    ...(selected && {
        // background: theme.palette.primary.dark,
        borderLeft: `solid 5px #6F4EE8`,
        // marginLeft: "-5px",
        paddingLeft: "17px",
        color: "white",
        ".MuiSvgIcon-root": {
            color: "white",
        },
    }),
}));

const StyledFlyoutListItem = styled(ListItemButton, {
    shouldForwardProp: (p) =>
        !["selected", "open", "popperOpen"].includes(p.toString()),
})<{
    selected: boolean;
    open: boolean;
    popperOpen: boolean;
}>(({ selected, theme, popperOpen }) => ({
    marginBottom: 8,
    paddingTop: "10px",
    paddingLeft: "22px",
    marginLeft: "-8px",
    paddingBottom: "12px",
    height: "48px",
    borderRadius: popperOpen ? "0px" : "0px 30px 30px 0px",
    color: theme.palette.primary.contrastText,
    overflow: "hidden",
    ".MuiSvgIcon-root": {
        color: theme.palette.primary.contrastText,
    },
    ".MuiListItemText-primary": {
        fontWeight: "bold",
        fontSize: "0.825rem",
    },
    ".MuiListItemAvatar-root": {
        marginLeft: -4,
        marginRight: 4,
        ".MuiAvatar-root": {
            height: "32px",
            width: "32px",
            fontSize: "11pt",
        },
    },
    "&:hover": {
        background: `rgba(${hexToRgb("#6F4EE8", "string")}, 0.2)`,
    },
    ...(popperOpen && {
        marginRight: "-8px",
    }),
    ...(selected && {
        // background: theme.palette.primary.dark,
        borderLeft: `solid 5px #6F4EE8`,
        // marginLeft: "-5px",
        paddingLeft: "17px",
        color: "white",
        ".MuiSvgIcon-root": {
            color: "white",
        },
    }),
}));

const StyledFlyout = styled(Popper)(({ theme }) => ({
    ".container": {
        borderRadius: "0px 30px 30px 0px",
        background: theme.palette.gradient?.main,
        color: "white",
        height: "100%",
        paddingRight: "4px",
        paddingLeft: "4px",
        ".MuiButton-root": {
            color: "white",
            marginTop: "4px",
            fontSize: "0.825rem",
        },
    },
    zIndex: 1,
    height: "48px",
    marginLeft: "18px",
}));

const DrawerHeader = styled("div")(() => ({
    width: "100%",
    display: "flex",
    padding: 8,
    flexWrap: "wrap",
    alignItems: "flex-end",
    color: "white",
    justifyContent: "center",
    ".MuiIconButton-root": {
        color: "white",
        marginLeft: "auto",
    },
}));

const roleClaimType =
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
const nameClaimType =
    "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name";

const Footer = () => {
    const dispatch = useAppDispatch();

    const {
        user: {
            ["first-name"]: firstName,
            ["last-name"]: lastName,
            [roleClaimType]: userType,
            [nameClaimType]: loginName,
        },
    } = useAppSelector((state) => state.authentication);

    const { sidebarOpen: open, alertsOpen } = useAppSelector(
        (state) => state.common
    );
    const { serverName } = useAppSelector((state) => state.yardidata);
    const { alerts } = useAppSelector((state) => state.config);

    const [anchorEl1, setAnchorEl1] = React.useState<HTMLElement | null>(null);
    const [anchorEl2, setAnchorEl2] = React.useState<HTMLElement | null>(null);
    const [popperOpen1, setPopperOpen1] = React.useState(false);
    const [popperOpen2, setPopperOpen2] = React.useState(false);

    const location = useLocation();
    const history = useHistory();

    const setOpen = (isOpen: boolean) => {
        if (isOpen) {
            dispatch(openSidebar());
        } else {
            dispatch(closeSidebar());
        }
    };

    const logout = () => {
        window.location.href = "/Account/Logout";
    };

    const redirect = (to: string) => {
        history.replace(`../${to}`);
    };

    const onCopyClick = () => {
        navigator.clipboard.writeText(serverName);
        dispatch(setSuccessMessage("Server name copied to clipboard!"));
    };

    React.useEffect(() => {
        dispatch(getServerName());
    }, []);

    const canBeOpen1 = popperOpen1 && Boolean(anchorEl1);
    const id1 = canBeOpen1 ? "popper" : undefined;

    const canBeOpen2 = popperOpen2 && Boolean(anchorEl2);
    const id2 = canBeOpen2 ? "popper" : undefined;

    return (
        <Fragment>
            <StyledFooter>
                <DrawerHeader>
                    <IconButton onClick={() => setOpen(!open)}>
                        {open ? <ChevronLeft /> : <ChevronRight />}
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <List>
                    {userType !== "33fadmin" && (
                        <>
                            {alerts.length > 0 ? (
                                <StyledBadge variant="dot" color="error">
                                    <StyledListItem
                                        onClick={() =>
                                            alertsOpen
                                                ? dispatch(closeAlerts())
                                                : dispatch(openAlerts())
                                        }
                                        selected={alertsOpen}
                                    >
                                        <ListItemIcon>
                                            <NotificationsNoneOutlined />
                                        </ListItemIcon>
                                        <ListItemText primary="Alerts" />
                                    </StyledListItem>
                                </StyledBadge>
                            ) : (
                                <StyledListItem
                                    onClick={() =>
                                        alertsOpen
                                            ? dispatch(closeAlerts())
                                            : dispatch(openAlerts())
                                    }
                                    selected={alertsOpen}
                                >
                                    <ListItemIcon>
                                        <NotificationsNoneOutlined />
                                    </ListItemIcon>
                                    <ListItemText primary="Alerts" />
                                </StyledListItem>
                            )}
                            <StyledListItem
                                onClick={() => redirect("documentation")}
                                selected={
                                    "documentation" ===
                                    location.pathname.split("/")[1]
                                }
                            >
                                <ListItemIcon>
                                    <HelpOutline />
                                </ListItemIcon>
                                <ListItemText primary="Support" />
                            </StyledListItem>
                        </>
                    )}
                    <StyledFlyoutListItem
                        open={open}
                        selected={canBeOpen1}
                        popperOpen={canBeOpen1}
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                            setAnchorEl1(event.currentTarget);
                            setPopperOpen1(!popperOpen1);
                        }}
                        aria-describedby={id1}
                    >
                        <ListItemIcon>
                            <LinkChain selected={canBeOpen1} />
                        </ListItemIcon>
                        <ListItemText primary="Server URL" />
                    </StyledFlyoutListItem>
                    <StyledFlyout
                        open={canBeOpen1}
                        id={id1}
                        placement="right"
                        anchorEl={anchorEl1}
                        transition
                    >
                        {({ TransitionProps }) => (
                            <Slide
                                {...TransitionProps}
                                timeout={200}
                                direction="right"
                            >
                                <Box className="container">
                                    <Button
                                        startIcon={
                                            <CopyCircle
                                                sx={{
                                                    fontSize: "28px !important",
                                                }}
                                            />
                                        }
                                        onClick={onCopyClick}
                                    >
                                        {serverName || "Loading..."}
                                    </Button>
                                </Box>
                            </Slide>
                        )}
                    </StyledFlyout>
                    <StyledFlyoutListItem
                        open={open}
                        selected={canBeOpen2}
                        popperOpen={canBeOpen2}
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                            setAnchorEl2(event.currentTarget);
                            setPopperOpen2(!popperOpen2);
                        }}
                        aria-describedby={id2}
                    >
                        <ListItemAvatar>
                            <Avatar
                                sx={{
                                    backgroundColor: getColorFromName(
                                        `${firstName} ${lastName}`
                                    ),
                                }}
                            >
                                {userType !== "33fadmin"
                                    ? `${firstName ? firstName.charAt(0) : ""}${
                                          lastName ? lastName.charAt(0) : ""
                                      }`
                                    : loginName.charAt(0)}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                userType !== "33fadmin"
                                    ? `${firstName} ${lastName}`
                                    : loginName
                            }
                        />
                    </StyledFlyoutListItem>
                    <StyledFlyout
                        open={canBeOpen2}
                        id={id2}
                        placement="right"
                        anchorEl={anchorEl2}
                        transition
                    >
                        {({ TransitionProps }) => (
                            <Slide
                                {...TransitionProps}
                                timeout={200}
                                direction="right"
                            >
                                <Box className="container">
                                    <Button
                                        startIcon={
                                            <LogoutRounded
                                                sx={{
                                                    fontSize: "28px !important",
                                                }}
                                            />
                                        }
                                        onClick={logout}
                                    >
                                        Logout
                                    </Button>
                                </Box>
                            </Slide>
                        )}
                    </StyledFlyout>
                </List>
            </StyledFooter>
        </Fragment>
    );
};

export default Footer;
